import { springs } from "@/components/ui/motion";
import { getPaletteColor } from "@/lib/color-palettes";
import type { UIAudit, UIAuditCategory } from "@/packages/types";
import { cn, useTranslation } from "@/packages/utils";
import { motion } from "framer-motion";
import { Calendar } from "lucide-react";
import type { WidgetVariant } from "./WidgetCard";
import { WidgetCard, WidgetCardSkeleton } from "./WidgetCard";

interface DeadlineCardProps {
  audit: UIAudit;
  categories?: UIAuditCategory[];
  index?: number;
  variant?: WidgetVariant;
}

function getDeadlineColor(days: number) {
  if (days > 14) return "text-emerald-400";
  if (days >= 7) return "text-amber-400";
  return "text-red-400";
}

function buildSparklineData(categories?: UIAuditCategory[]): number[] {
  if (!categories?.length) return [];

  const now = Date.now();
  const dayMs = 86400000;
  const days = Array.from({ length: 14 }, () => 0);

  for (const cat of categories) {
    for (const item of cat.items) {
      const ts = item.lastModified;
      if (!ts) continue;
      const daysAgo = Math.floor((now - ts) / dayMs);
      if (daysAgo >= 0 && daysAgo < 14) {
        days[13 - daysAgo]++;
      }
    }
  }

  return days;
}

function buildAreaPath(
  data: number[],
  width: number,
  height: number,
): { linePath: string; areaPath: string } {
  const max = Math.max(...data, 1);
  const stepX = width / (data.length - 1);
  const points = data.map((v, i) => ({
    x: i * stepX,
    y: height - (v / max) * (height - 2),
  }));

  // Smooth curve using cubic bezier
  let linePath = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const cpx1 = prev.x + stepX * 0.4;
    const cpx2 = curr.x - stepX * 0.4;
    linePath += ` C ${cpx1} ${prev.y}, ${cpx2} ${curr.y}, ${curr.x} ${curr.y}`;
  }

  const areaPath = `${linePath} L ${points[points.length - 1].x} ${height} L ${points[0].x} ${height} Z`;

  return { linePath, areaPath };
}

export function DeadlineCard({
  audit,
  categories,
  index,
  variant,
}: DeadlineCardProps) {
  const { t } = useTranslation("audits");

  if (!audit.deadline) {
    return (
      <WidgetCard
        size="auto"
        title={t("overview.deadline.title")}
        icon={<Calendar size={16} />}
        index={index}
        variant={variant}
      >
        <div className="flex items-center justify-center h-full">
          <span className="text-sm text-muted">
            {t("overview.deadline.noDeadlineSet")}
          </span>
        </div>
      </WidgetCard>
    );
  }

  const daysRemaining = Math.ceil((audit.deadline - Date.now()) / 86400000);
  const isOverdue = daysRemaining < 0;
  const elapsed = Math.max(
    0,
    Math.min(
      1,
      (Date.now() - audit.createdAt) / (audit.deadline - audit.createdAt),
    ),
  );

  const sparkline = buildSparklineData(categories);
  const hasActivity = sparkline.some((v) => v > 0);
  const svgWidth = 200;
  const svgHeight = 28;
  const paths = hasActivity
    ? buildAreaPath(sparkline, svgWidth, svgHeight)
    : null;

  return (
    <WidgetCard
      size="auto"
      title={t("overview.deadline.title")}
      icon={<Calendar size={16} />}
      index={index}
      variant={variant}
      className={cn(isOverdue && "bg-red-400/[0.04] !border-red-400/20")}
    >
      <div className="flex flex-col justify-between h-full">
        <div>
          {isOverdue ? (
            <motion.span
              className="text-3xl font-bold tabular-nums text-red-400"
              animate={{ scale: [1, 1.02, 1] }}
              transition={{
                repeat: Number.POSITIVE_INFINITY,
                duration: 3,
                ease: "easeInOut",
              }}
            >
              {t("overview.deadline.overdue")}
            </motion.span>
          ) : (
            <span
              className={`text-3xl font-bold tabular-nums ${getDeadlineColor(daysRemaining)}`}
            >
              {daysRemaining}
              <span className="text-sm font-normal text-muted ml-1">
                {t("overview.deadline.daysLeft")}
              </span>
            </span>
          )}
        </div>

        {/* Time elapsed bar with gradient */}
        <div className="h-1.5 rounded-full bg-border w-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{
              background: isOverdue
                ? getPaletteColor("deadlines", 1)
                : "var(--color-accent)",
            }}
            initial={{ width: 0 }}
            animate={{ width: isOverdue ? "100%" : `${elapsed * 100}%` }}
            transition={{ ...springs.smooth, delay: 0.2 }}
          />
        </div>

        {/* Activity sparkline — smooth area chart */}
        {hasActivity && paths && (
          <svg
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            className="w-full mt-1"
            preserveAspectRatio="none"
            style={{ height: 28 }}
            role="img"
            aria-label="Activity sparkline"
          >
            <defs>
              <linearGradient
                id={isOverdue ? "sparkline-fill-red" : "sparkline-fill"}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor={
                    isOverdue
                      ? getPaletteColor("deadlines", 1)
                      : getPaletteColor("deadlines", 3)
                  }
                  stopOpacity="0.18"
                />
                <stop
                  offset="100%"
                  stopColor={
                    isOverdue
                      ? getPaletteColor("deadlines", 1)
                      : getPaletteColor("deadlines", 3)
                  }
                  stopOpacity="0"
                />
              </linearGradient>
            </defs>
            <path
              d={paths.areaPath}
              fill={`url(#${isOverdue ? "sparkline-fill-red" : "sparkline-fill"})`}
            />
            <path
              d={paths.linePath}
              fill="none"
              stroke={
                isOverdue
                  ? getPaletteColor("deadlines", 1)
                  : getPaletteColor("deadlines", 3)
              }
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        )}

        <span className="text-xs text-muted">
          {t("overview.deadline.due", {
            date: new Date(audit.deadline).toLocaleDateString(),
          })}
        </span>
      </div>
    </WidgetCard>
  );
}

export function DeadlineCardSkeleton({ index }: { index?: number }) {
  return <WidgetCardSkeleton size="auto" index={index} />;
}
